import { Box, Button, Typography } from '@mui/material';
import BasicModal from 'common/components/modal/BasicModal';
import LoadingScreen from '@common/components/LoadingScreen';
import { useEffect, useState } from 'react';

type MapTestModalProps = {
  open: boolean;
  subject?: string;
  showDoneBtn: boolean;
  startTest: () => void;
  advanceStep: () => void;
  cancel: () => void;
  proctorLoading?: boolean;
  emojiCount?: number;
};

// Assuming this is in the file where MapTestProps is defined
interface MapTestProps {
  advanceStep: (grade?: number) => void;
  mapDetails?: {
    studentId: string;
    name: string;
    email: string;
    age_grade: number;
  };
}

// Extend the Window interface to include Tawk_API
declare global {
  // AI-GEN - Cursor - GPT4
  interface Window {
    // AI-GEN - Cursor - GPT4
    Tawk_API: any; // AI-GEN - Cursor - GPT4
    Tawk_LoadStart: Date; // AI-GEN - Cursor - GPT4
  } // AI-GEN - Cursor - GPT4
} // AI-GEN - Cursor - GPT4

// Tawk has bad handling of cookies on their ends and conversations become endless loops of feedback
// This aims to remove those
const removeTawkSpecifiedCookies = (): void => {
  // AI-GEN - Cursor - GPT4
  const cookies: string[] = document.cookie.split(';'); // AI-GEN - Cursor - GPT4

  // Function to remove a cookie
  const removeCookie = (name: string): void => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`; // AI-GEN - Cursor - GPT4
  };

  // Specific cookies to remove
  const specificCookies: string[] = ['TawkConnectionTime', 'twk_idm_key']; // AI-GEN - Cursor - GPT4

  // Remove specific cookies
  specificCookies.forEach((cookieName: string) => {
    if (document.cookie.includes(cookieName)) {
      removeCookie(cookieName); // AI-GEN - Cursor - GPT4
    }
  });

  // Remove cookies that start with 'twk_uuid_'
  cookies.forEach((cookie: string) => {
    const trimmedCookie: string = cookie.trim(); // AI-GEN - Cursor - GPT4
    const cookieName: string = trimmedCookie.split('=')[0]; // AI-GEN - Cursor - GPT4
    if (cookieName.startsWith('twk_uuid_')) {
      removeCookie(cookieName); // AI-GEN - Cursor - GPT4
    }
  });
};

function MapTestModal({
  open,
  showDoneBtn,
  subject,
  startTest,
  advanceStep,
  cancel,
  proctorLoading,
  emojiCount,
}: MapTestModalProps) {
  return (
    <>
      <BasicModal
        open={open}
        onClose={() => {
          cancel();
        }}
      >
        <Box>
          <Typography variant="body1" marginY={2}>
            Are you set for your {subject} test. Here's what you need to do:
          </Typography>

          <Typography variant="body1" marginY={2} component="div">
            <div>
              {' '}
              {/* AI-GEN - Cursor - GPT4 */}
              <strong>Before You Start:</strong>
            </div>
            <ul style={{ marginLeft: 32 }}>
              {' '}
              {/* AI-GEN - Cursor - GPT4 */}
              <li>
                {' '}
                {/* AI-GEN - Cursor - GPT4 */}
                <strong>Tech Check:</strong> Run a quick {/* AI-GEN - Cursor - GPT4 */}
                <a href="https://check.nwea.org/" target="_blank">
                  {' '}
                  {/* AI-GEN - Cursor - GPT4 */}
                  Workstation Diagnostic
                </a>{' '}
                {/* AI-GEN - Cursor - GPT4 */}
                to check your computer is ready.
              </li>{' '}
              {/* AI-GEN - Cursor - GPT4 */}
              <li>
                {' '}
                {/* AI-GEN - Cursor - GPT4 */}
                <strong>Quiet Zone:</strong> Find a peaceful spot where you can concentrate without{' '}
                {/* AI-GEN - Cursor - GPT4 */}
                interruptions.
              </li>{' '}
              {/* AI-GEN - Cursor - GPT4 */}
            </ul>{' '}
            {/* AI-GEN - Cursor - GPT4 */}
          </Typography>

          <Typography variant="body1" marginY={2} component="div">
            <strong>While You're Testing</strong>
            <ol style={{ marginLeft: 32 }}>
              <li>
                <strong>Stay Chill and Try Hard:</strong> Take your time and try your best on each
                question.
              </li>
              <li>
                <strong>Take a break:</strong> If you're tired or need to go to the bathroom, tell
                your proctor, and they'll pause the test. You can start again when you're back.
              </li>
            </ol>
          </Typography>

          <Typography variant="body1" marginY={2}>
            <strong>Ready to Start?</strong>
            <br />
            When you've got your thinking cap on, press "Connect with My Proctor" to start.
          </Typography>

          <Typography variant="body1" marginY={2}>
            <strong>Need Help?</strong>
            <br />
            If you're stuck or just want to ask something, your test helper is ready to listen.
          </Typography>

          <Typography variant="body1" marginY={2}>
            Good luck, and remember, just do your best!
          </Typography>

          <Box display="flex" justifyContent="center">
            {proctorLoading ? (
              <Box display="flex" justifyContent="center" flexDirection="column">
                <Typography variant="body1" marginY={2}>
                  Loading Chat to connect to your proctor
                </Typography>
                <Typography variant="body1" marginY={2} fontSize={36} textAlign="center">
                  {'👉'.repeat(emojiCount ?? 0)}
                </Typography>
                <LoadingScreen positionFixed={false} />
              </Box>
            ) : (
              <Button variant="outlined" sx={{ padding: 2, marginRight: 1 }} onClick={startTest}>
                Connect with My Proctor
              </Button>
            )}
          </Box>

          {showDoneBtn && (
            <>
              <Typography variant="body1" marginY={2}>
                <strong>Done with the MAP Test?</strong> Click the button below:
              </Typography>
              <Box width="100%" display="flex" justifyContent="center" marginY={4}>
                <Button variant="outlined" sx={{ padding: 2 }} onClick={advanceStep}>
                  I'm Done with the MAP Test
                </Button>
              </Box>
            </>
          )}
        </Box>
      </BasicModal>
    </>
  );
}

export function MapTest({ advanceStep, mapDetails }: MapTestProps) {
  const [activeTest, setActiveTest] = useState<string>();
  const [showDoneBtn, setShowDoneBtn] = useState<boolean>(
    localStorage.getItem('doneClick') === 'true'
  ); // AI-GEN - Cursor - GPT4
  const [showTestModal, setShowTestModal] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [proctorLoading, setProctorLoading] = useState(false);
  const [emojiCount, setEmojiCount] = useState(1); // AI-GEN - Cursor - GPT4

  console.log(`mapDetails: ${JSON.stringify(mapDetails)}`);

  const startTest = () => {
    setProctorLoading(true);
    const checkIframes = setInterval(() => {
      const iframes = document.querySelectorAll('iframe'); // AI-GEN - Cursor - GPT4
      if (iframes.length >= 2) {
        setProctorLoading(false); // AI-GEN - Cursor - GPT4
        clearInterval(checkIframes); // AI-GEN - Cursor - GPT4
        iframes.forEach((iframe: HTMLIFrameElement) => {
          // AI-GEN - Cursor - GPT4
          const style = document.createElement('style'); // AI-GEN - Cursor - GPT4
          style.textContent = `
            .tawk-branding {
              display: none;
            }
            .tawk-dropdown-menu > div > button:nth-child(4) {
              display: none !important;
            }
            .tawk-dropdown-menu > div > a {
              display: none !important;
            }
          `; // AI-GEN - Cursor - GPT4
          console.log('Appending style to iframe.'); // AI-GEN - Cursor - GPT4
          iframe.contentDocument?.head.appendChild(style); // AI-GEN - Cursor - GPT4
        });
        window.Tawk_API.maximize(); // AI-GEN - Cursor - GPT4
        window.Tawk_API.setAttributes(
          // AI-GEN - Cursor - GPT4
          {
            // AI-GEN - Cursor - GPT4
            name: mapDetails?.name, // AI-GEN - Cursor - GPT4
            email: mapDetails?.email, // AI-GEN - Cursor - GPT4
            subject: 'Math', // AI-GEN - Cursor - GPT4
            studentId: mapDetails?.studentId, // AI-GEN - Cursor - GPT4
            grade: mapDetails?.age_grade, // AI-GEN - Cursor - GPT4
          }, // AI-GEN - Cursor - GPT4
          function (error: Error) {
            // AI-GEN - Cursor - GPT4
            if (error) {
              // AI-GEN - Cursor - GPT4
              console.log(`Attributes not set. Error: ${error}`);
            } else {
              console.log('Attributes set successfully');
            }
          } // AI-GEN - Cursor - GPT4
        ); // AI-GEN - Cursor - GPT4
      } else {
        setEmojiCount((prevCount) => (prevCount < 3 ? prevCount + 1 : 1)); // AI-GEN - Cursor - GPT4
      }
    }, 500); // Check every half second // AI-GEN - Cursor - GPT4
    localStorage.setItem('tawkRan', '0');
    if (document.getElementById('tawk_chat')) {
      setTimeout(() => {
        // AI-GEN - Cursor - GPT4
        setShowDoneBtn(true); // AI-GEN - Cursor - GPT4
      }, 3000); // AI-GEN - Cursor - GPT4
      localStorage.setItem('doneClick', 'true'); // AI-GEN - Cursor - GPT4
      return;
    }
    // AI-GEN - Cursor - GPT4
    const script = document.createElement('script'); // AI-GEN - Cursor - GPT4
    script.async = true; // AI-GEN - Cursor - GPT4
    script.src = 'https://embed.tawk.to/65a17bd98d261e1b5f529017/1hjvcnic0'; // AI-GEN - Cursor - GPT4
    script.charset = 'UTF-8'; // AI-GEN - Cursor - GPT4
    script.setAttribute('crossorigin', '*'); // AI-GEN - Cursor - GPT4
    script.id = 'tawk_chat'; // AI-GEN - Cursor - GPT4
    document.head.appendChild(script); // AI-GEN - Cursor - GPT4

    script.onload = () => {
      // AI-GEN - Cursor - GPT4
      window.Tawk_API = window.Tawk_API || {}; // AI-GEN - Cursor - GPT4
      window.Tawk_LoadStart = new Date(); // AI-GEN - Cursor - GPT4
      window.Tawk_API.onLoad = function () {
        // AI-GEN - Cursor - GPT4
        window.Tawk_API.maximize(); // AI-GEN - Cursor - GPT4
        window.Tawk_API.setAttributes(
          // AI-GEN - Cursor - GPT4
          {
            // AI-GEN - Cursor - GPT4
            name: mapDetails?.name, // AI-GEN - Cursor - GPT4
            email: mapDetails?.email, // AI-GEN - Cursor - GPT4
            subject: 'Math', // AI-GEN - Cursor - GPT4
            studentId: mapDetails?.studentId, // AI-GEN - Cursor - GPT4
            grade: mapDetails?.age_grade, // AI-GEN - Cursor - GPT4
          }, // AI-GEN - Cursor - GPT4
          function (error: Error) {
            // AI-GEN - Cursor - GPT4
            if (error) {
              // AI-GEN - Cursor - GPT4
              console.log(`Attributes not set. Error: ${error}`);
            } else {
              console.log('Attributes set successfully');
            }
          } // AI-GEN - Cursor - GPT4
        ); // AI-GEN - Cursor - GPT4
      }; // AI-GEN - Cursor - GPT4

      setTimeout(() => {
        // AI-GEN - Cursor - GPT4
        setShowDoneBtn(true); // AI-GEN - Cursor - GPT4
      }, 3000); // AI-GEN - Cursor - GPT4
      localStorage.setItem('doneClick', 'true'); // AI-GEN - Cursor - GPT4
    };
  };

  const finishMap = () => {
    setLoading(true);
    localStorage.removeItem('doneClick'); // AI-GEN - Cursor - GPT4
    const script = document.getElementById('tawk_chat');
    if (script) {
      script.remove();
      const iframes = document.querySelectorAll('iframe'); // AI-GEN - Cursor - GPT4
      iframes.forEach((iframe) => {
        iframe.parentNode?.removeChild(iframe); // AI-GEN - Cursor - GPT4
      }); // AI-GEN - Cursor - GPT4
    }
    advanceStep();
  };

  const selectMapTest = (subject: string) => {
    setActiveTest(subject);
    setShowTestModal(true);
  };

  useEffect(() => {
    // AI-GEN - Cursor - GPT4
    removeTawkSpecifiedCookies(); // AI-GEN - Cursor - GPT4
    localStorage.removeItem('tawkRan'); // AI-GEN - Cursor - GPT4
    localStorage.setItem('tawkRan', '0'); // AI-GEN - Cursor - GPT4
  }, []); // AI-GEN - Cursor - GPT4

  return loading ? (
    <LoadingScreen positionFixed={false} />
  ) : (
    <>
      <Typography variant="h3" marginBottom={4}>
        My assigned MAP growth tests
      </Typography>
      <Typography variant="body1" marginY={2}>
        Hello, young learner!
      </Typography>

      <Typography variant="body1" marginY={2}>
        It's time for our MAP tests. These special tests are like a game that shows how much you've
        learned, just like how you grow taller and can do more things as you get bigger.
      </Typography>

      <Typography variant="body1" marginY={2}>
        Some questions might be easy-peasy, and others might make you think really hard, but that's
        perfectly fine! Every question is a chance to show what you know and discover new things to
        learn.
      </Typography>

      <Typography variant="body1" marginY={2}>
        <a
          href="https://studentresources.nwea.org/app/k_two/what's_this_test_about.html"
          target="_blank"
        >
          Watch the Fun Explainer Video
        </a>
        &nbsp; to learn more!
      </Typography>

      <Typography variant="body1" marginY={2}>
        Take your time and do your best; there's no hurry. Each question helps us understand how to
        make learning fun for you!
      </Typography>

      <Typography variant="body1" marginY={2}>
        Choose a test to start your adventure:
      </Typography>

      <Typography variant="body1" marginY={2}>
        Remember, take at most one test per day! 🧠
      </Typography>

      <Box display="flex" justifyContent="center" marginY={4}>
        <Button variant="outlined" sx={{ padding: 2 }} onClick={() => selectMapTest('Math')}>
          🔢 Take Math test
        </Button>
      </Box>

      <Box display="flex" justifyContent="center" marginY={4}>
        {showDoneBtn && !showTestModal && (
          <>
            <Box display="flex" flexDirection="column" justifyContent="center" marginY={4}>
              {' '}
              {/*// AI-GEN - Cursor - GPT4 */}
              <Typography variant="body1" marginY={2}>
                <strong>Done with the MAP Test?</strong> Click the button below:
              </Typography>
              <Button variant="outlined" sx={{ padding: 2 }} onClick={finishMap}>
                I'm Done with the MAP Test
              </Button>
            </Box>
          </>
        )}
      </Box>

      <MapTestModal
        open={showTestModal}
        subject={activeTest}
        showDoneBtn={showDoneBtn}
        startTest={startTest}
        cancel={() => setShowTestModal(false)}
        advanceStep={finishMap}
        proctorLoading={proctorLoading}
        emojiCount={emojiCount}
      />
    </>
  );
}
